import React, { useState, useEffect } from 'react';
import { UserGroupIcon, BookOpenIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import StatCard from '../components/StatCard';
import { apiUrl, authFetch, authService, booksApi, unitCodesApi } from '../utils/api';

const Dashboard = () => {
  const navigate = useNavigate();
  const [studentCount, setStudentCount] = useState<number>(0);
  const [totalCodes, setTotalCodes] = useState<number>(0);
  const [totalCourses, setTotalCourses] = useState<number>(0);
  const [totalBooks, setTotalBooks] = useState<number>(0);
  const [totalUnitCodes, setTotalUnitCodes] = useState<number>(0);
  const [isLoadingStudents, setIsLoadingStudents] = useState<boolean>(true);
  const [isLoadingCodes, setIsLoadingCodes] = useState<boolean>(true);
  const [isLoadingCourses, setIsLoadingCourses] = useState<boolean>(true);
  const [isLoadingBooks, setIsLoadingBooks] = useState<boolean>(true);
  const [isLoadingUnitCodes, setIsLoadingUnitCodes] = useState<boolean>(true);

  // This would typically come from an API
  const stats = {
    totalCourses: 48,
  };

  useEffect(() => {
    const fetchStudentCount = async () => {
      setIsLoadingStudents(true);
      try {
        const response = await authFetch(apiUrl('AdminStudent/count'), {
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch student count');
        }
        
        // The API returns a plain text number, not JSON
        const result = await response.text();
        
        // Convert the text to a number
        const count = parseInt(result, 10);
        
        // If it's a valid number, set it
        if (!isNaN(count)) {
          setStudentCount(count);
        } else {
          console.error('Invalid student count response:', result);
          setStudentCount(0);
        }
      } catch (error) {
        console.error('Error fetching student count:', error);
        // Keep the default value in case of error
      } finally {
        setIsLoadingStudents(false);
      }
    };

    const fetchTotalCodes = async () => {
      setIsLoadingCodes(true);
      try {
        const response = await authFetch(apiUrl('CourseCode/GetAllCodesCount'), {
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch total codes count');
        }
        
        const result = await response.json();
        
        if (result.success) {
          setTotalCodes(result.data);
        } else {
          console.error('Error in API response:', result.message);
          setTotalCodes(0);
        }
      } catch (error) {
        console.error('Error fetching total codes count:', error);
        setTotalCodes(0);
      } finally {
        setIsLoadingCodes(false);
      }
    };

    const fetchTotalCourses = async () => {
      setIsLoadingCourses(true);
      try {
        const response = await authFetch(apiUrl('Course/count'), {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch total courses count');
        }
        const result = await response.json();
        if (typeof result.totalCourses === 'number') {
          setTotalCourses(result.totalCourses);
        } else {
          setTotalCourses(0);
        }
      } catch (error) {
        console.error('Error fetching total courses count:', error);
        setTotalCourses(0);
      } finally {
        setIsLoadingCourses(false);
      }
    };

    const fetchTotalBooks = async () => {
      setIsLoadingBooks(true);
      try {
        const response = await booksApi.getBooks(1, 1); // Get first page with 1 item to get total count
        if (response.success) {
          setTotalBooks(response.data.totalCount || 0);
        } else {
          setTotalBooks(0);
        }
      } catch (error) {
        console.error('Error fetching total books count:', error);
        setTotalBooks(0);
      } finally {
        setIsLoadingBooks(false);
      }
    };

    const fetchTotalUnitCodes = async () => {
      setIsLoadingUnitCodes(true);
      try {
        const response = await unitCodesApi.getAllCodesCount();
        if (response.success) {
          setTotalUnitCodes(response.data || 0);
        } else {
          setTotalUnitCodes(0);
        }
      } catch (error) {
        console.error('Error fetching total unit codes count:', error);
        setTotalUnitCodes(0);
      } finally {
        setIsLoadingUnitCodes(false);
      }
    };

    fetchStudentCount();
    fetchTotalCodes();
    fetchTotalCourses();
    fetchTotalBooks();
    fetchTotalUnitCodes();
  }, []);

  const handleCardClick = (path: string) => {
    navigate(path);
  };

  return (
    <div className="p-4 sm:p-6" dir="rtl">
      <h1 className="text-2xl sm:text-3xl font-bold text-dark mb-6 sm:mb-8">لوحة التحكم</h1>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
        <StatCard
          title="إجمالي الطلاب"
          value={studentCount}
          icon={<UserGroupIcon className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />}
          onClick={() => handleCardClick('/students')}
          isLoading={isLoadingStudents}
        />

        <StatCard
          title="إجمالي الباقات"
          value={totalCourses}
          icon={<BookOpenIcon className="w-6 h-6 sm:w-8 sm:h-8 text-secondary" />}
          onClick={() => handleCardClick('/courses')}
          isLoading={isLoadingCourses}
        />

        <StatCard
          title="إجمالي الكتب"
          value={totalBooks}
          icon={
            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0118 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
            </svg>
          }
          onClick={() => handleCardClick('/books')}
          isLoading={isLoadingBooks}
        />

        <StatCard
          title="أكواد المحاضرات"
          value={totalUnitCodes}
          icon={
            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
            </svg>
          }
          onClick={() => handleCardClick('/unit-codes')}
          isLoading={isLoadingUnitCodes}
        />

        <StatCard
          title="أكواد الباقات"
          value={totalCodes}
          icon={
            <svg className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
            </svg>
          }
          onClick={() => handleCardClick('/codes')}
          isLoading={isLoadingCodes}
        />
      </div>

    </div>
  );
};

export default Dashboard; 